import { Link } from "react-router-dom";
import { useEffect } from "react";
import Logo from "../components/Logo";
import {
  ArrowRight, CheckCircle, Sparkle, Truck, CurrencyDollar,
  Clock, Quotes, ShieldCheck, Star, PlayCircle,
} from "@phosphor-icons/react";
import { trackConversion } from "../lib/analytics";
import { useExperiments } from "../lib/experiments";

/*
  MetaAdLandingPage — dedicated landing page for the Meta ad campaign
  "FPA - Small Fleet Owners - Test".

  Route: /lp/small-fleet

  Design rules for max conversion:
    - ONE CTA above the fold. No nav bar distractions.
    - Headline mirrors the Meta ad copy exactly (attention continuity).
    - Numbers-first (real customer data), not fluff.
    - Trust bar (Stripe, FMCSA, 4.9 rating) below hero.
    - Sticky CTA button follows on scroll for mobile.
    - Fires a landing_view pixel event on mount for retargeting.

  Ad targets small-fleet OWNERS whose #1 pain is broker rate leakage.
  Every section drives back to the same core message: "Stop leaving
  $80-150 per load on the table."
*/

const SIGNUP_URL =
  "/signup?utm_source=meta&utm_medium=cpc&utm_campaign=fpa_smallfleet_test&lp=small_fleet";

const METRICS = [
  { value: "+$118K", label: "in broker rate uplift over 90 days" },
  { value: "22 hrs/wk", label: "of dispatcher time saved" },
  { value: "40 sec", label: "average time to send a counter-rate" },
  { value: "97%", label: "on-time delivery rate" },
];

const CHECKLIST = [
  "AI Rate Negotiator drafts + sends broker counter-offers with lane-benchmarked pricing",
  "AI Copilot reads driver texts + drafts detention invoices automatically",
  "One-tap PDF invoice generation with your logo & payment link",
  "Mobile driver portal — POD uploads via phone camera, no app to install",
  "Live diesel + FMCSA + Weather signals wired into dispatch",
  "Weekly business digest emailed to you every Monday 6am",
];

const FAQS = [
  {
    q: "Do I need a credit card to start?",
    a: "Yes — a card on file, but $0 today. You get 7 free days with everything unlocked; cancel anytime before day 8 and you're never charged. Partner links take 50% off your first month.",
  },
  {
    q: "How is this different from McLeod / Prophesy / TruckingOffice?",
    a: "Those are enterprise TMS systems that cost $500-2000/mo and take 3-6 months to onboard. FreightPilot is $79-499/mo, day-one usable, and built AI-first — the copilot reads your rate cons and drafts broker emails and counter-offers for you to approve.",
  },
  {
    q: "How many trucks does this work for?",
    a: "Solo owner-operators up to fleets of 100 trucks. Fleets above 100 should reach out for our Enterprise plan.",
  },
  {
    q: "How long until I see results?",
    a: "Most carriers save 5-8 hours in their first week just from the AI dispatch inbox. The Rate Negotiator typically raises accepted rates by $80-150 per load starting from week 2 as it learns your lanes.",
  },
];

export default function MetaAdLandingPage() {
  const { getSlot } = useExperiments("/lp/small-fleet");

  useEffect(() => {
    document.title = "Small-Fleet Ops Copilot — 7-Day Free Trial | FreightPilot AI";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Stop leaving $80-150 per load on the table. FreightPilot AI drafts your counter-offers, invoices, and driver updates. 7-day free trial, cancel anytime."
      );
    }
    // Fire the landing view event for Meta Pixel retargeting audience.
    trackConversion("landing_view", { landing_page: "meta_ad_small_fleet" });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg" data-testid="meta-ad-landing-page">
      <MinimalHeader getSlot={getSlot} />
      <Hero getSlot={getSlot} />
      <TrustBar getSlot={getSlot} />
      <MetricsGrid />
      <WhatYouGet />
      <SocialProofQuote />
      <FAQ />
      <FinalCTA getSlot={getSlot} />
      <MinimalFooter />
      <StickyCTA />
    </div>
  );
}

/* ─── HEADER (no nav — ad LPs stay focused) ─── */
function MinimalHeader({ getSlot }) {
  const ctaLabel = getSlot("lp_smallfleet_header_cta_v1", "Start free trial");
  return (
    <header className="border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" data-testid="lp-home-link"><Logo variant="sm" /></Link>
        <a
          href={SIGNUP_URL}
          data-testid="lp-header-cta"
          onClick={() => trackConversion("signup_click", { source: "lp_header" })}
          className="text-xs sm:text-sm rounded-full px-4 sm:px-5 py-2 border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors font-medium"
        >
          {ctaLabel}
        </a>
      </div>
    </header>
  );
}

/* ─── HERO — mirrors Meta ad copy ─── */
function Hero({ getSlot }) {
  // A/B slots. Default fallback = current control copy so removing/pausing the
  // experiment reverts to the shipping version with zero code changes.
  const headline = getSlot(
    "lp_smallfleet_hero_headline_v1",
    "Small fleets are leaving $80-$150 per load on the table.",
  );
  const primaryCta = getSlot(
    "lp_smallfleet_hero_cta_v1",
    "Start 7-day free trial",
  );
  const subtitle = getSlot(
    "lp_smallfleet_hero_subtitle_v1",
    "The AI Rate Negotiator inside FreightPilot AI counters brokers in 40 seconds with lane-benchmarked pricing. One real customer: +$118,000 in broker rate uplift over 90 days.",
  );
  return (
    <section
      className="relative py-14 sm:py-24 border-b border-white/10 overflow-hidden"
      data-testid="lp-hero"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-8 relative">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/25 bg-[#FF5A1F]/5 text-[11px] uppercase tracking-widest text-[#FF9764] mb-6"
          data-testid="lp-hero-pill"
        >
          <Sparkle size={12} weight="fill" /> FOR SMALL FLEETS + OWNER-OPS
        </div>

        <h1
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.03] tracking-tight mb-6 max-w-4xl"
          data-testid="lp-hero-headline"
        >
          {headline}
        </h1>
        <p
          className="text-neutral-300 text-lg sm:text-xl leading-relaxed max-w-2xl mb-8"
          data-testid="lp-hero-subtitle"
        >
          {subtitle}
        </p>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-6">
          <a
            href={SIGNUP_URL}
            onClick={() => trackConversion("signup_click", { source: "lp_hero_primary" })}
            data-testid="lp-hero-primary-cta"
            className="btn-primary rounded-full px-7 py-4 text-sm sm:text-base font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            {primaryCta} <ArrowRight size={16} weight="bold" />
          </a>
          <a
            href="/demo?utm_source=meta&utm_medium=cpc&utm_campaign=fpa_smallfleet_test"
            onClick={() => trackConversion("demo_click", { source: "lp_hero_secondary" })}
            data-testid="lp-hero-secondary-cta"
            className="rounded-full px-7 py-4 text-sm sm:text-base border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            <PlayCircle size={16} weight="fill" /> Watch 90-sec demo
          </a>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-500">
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle size={13} weight="fill" className="text-emerald-400" />
            $0 today · card on file
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle size={13} weight="fill" className="text-emerald-400" />
            Cancel anytime
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle size={13} weight="fill" className="text-emerald-400" />
            Setup in 3 minutes
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle size={13} weight="fill" className="text-emerald-400" />
            US-based support
          </span>
        </div>
      </div>
      {/* Decorative orange glow */}
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#FF5A1F]/15 rounded-full blur-[100px] pointer-events-none" />
    </section>
  );
}

/* ─── TRUST BAR ─── */
function TrustBar({ getSlot }) {
  // The 3rd item (social-proof rating) is the highest-value slot to A/B test —
  // rating vs number of carriers vs testimonial count all move differently for
  // small-fleet vs owner-op audiences. Wired first as an experiment slot.
  const socialProofLabel = getSlot(
    "lp_smallfleet_trust_stat_v1",
    "4.9 / 5 from 127 pilot carriers",
  );
  const items = [
    { icon: ShieldCheck, label: "FMCSA-verified carriers only" },
    { icon: CurrencyDollar, label: "Payments powered by Stripe" },
    { icon: Star, label: socialProofLabel },
    { icon: Truck, label: "4,300+ trucks under management" },
  ];
  return (
    <section className="py-8 sm:py-10 border-b border-white/10 bg-white/[0.015]" data-testid="lp-trust-bar">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <div key={i} className="flex items-center gap-2.5 text-xs sm:text-sm text-neutral-400">
              <Icon size={16} weight="fill" className="text-[#FF9764] shrink-0" />
              {it.label}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─── METRICS ─── */
function MetricsGrid() {
  return (
    <section className="py-16 sm:py-20 border-b border-white/10" data-testid="lp-metrics">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-10">
          <div className="overline mb-3">REAL NUMBERS · REAL CARRIERS</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            The numbers your dispatcher wishes she had.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
          {METRICS.map((m, i) => (
            <div key={i} className="card-surface p-6 text-center" data-testid={`lp-metric-${i}`}>
              <div className="font-display text-3xl sm:text-4xl font-semibold text-[#FF5A1F] tracking-tight">
                {m.value}
              </div>
              <div className="text-xs sm:text-sm text-neutral-400 mt-2 leading-snug">
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── WHAT YOU GET ─── */
function WhatYouGet() {
  return (
    <section className="py-16 sm:py-24 border-b border-white/10" data-testid="lp-features">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <div className="mb-10">
          <div className="overline mb-3">WHAT'S IN THE BOX</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
            Every dispatcher tool a small fleet needs.
            <br />
            <span className="text-[#FF5A1F]">Built AI-first.</span>
          </h2>
        </div>
        <ul className="space-y-4">
          {CHECKLIST.map((item, i) => (
            <li key={i} className="flex items-start gap-4" data-testid={`lp-feature-${i}`}>
              <CheckCircle size={20} weight="fill" className="text-[#FF5A1F] shrink-0 mt-1" />
              <span className="text-sm sm:text-base text-neutral-300 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─── SOCIAL PROOF QUOTE ─── */
function SocialProofQuote() {
  return (
    <section className="py-16 sm:py-24 border-b border-white/10" data-testid="lp-quote">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <div className="card-surface p-8 sm:p-12 relative">
          <Quotes
            size={48}
            weight="fill"
            className="text-[#FF5A1F]/25 absolute top-6 left-6"
          />
          <div className="pl-14 sm:pl-16">
            <p className="text-lg sm:text-2xl leading-[1.5] text-white/95 font-light mb-8">
              The AI Rate Negotiator did in 40 seconds what my brother-in-law
              used to fight brokers 20 minutes for. It knows the lane. It pulls
              the DAT rate. It sends the counter. I read the reply on my phone
              at the yard.
            </p>
            <div className="flex items-center gap-4 pt-6 border-t border-white/10">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#B54313] flex items-center justify-center text-black font-semibold text-base shrink-0">
                AD
              </div>
              <div>
                <div className="text-base text-white font-semibold">Angela D.</div>
                <div className="text-xs sm:text-sm text-neutral-500 mt-0.5">
                  Ops Manager · Reefer, 38 trucks · GA + AL lanes
                </div>
              </div>
              <div className="ml-auto text-right hidden sm:block">
                <div className="text-2xl font-display font-semibold text-emerald-400">+$118K</div>
                <div className="text-[10px] text-neutral-500 uppercase tracking-widest">90d rate uplift</div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 text-center">
          <Link
            to="/customers"
            className="text-sm text-[#FF9764] hover:text-[#FFB08A] inline-flex items-center gap-1.5"
          >
            Read more customer stories <ArrowRight size={13} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ ─── */
function FAQ() {
  return (
    <section className="py-16 sm:py-24 border-b border-white/10" data-testid="lp-faq">
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-10">
          <div className="overline mb-3">COMMON QUESTIONS</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            Things owner-ops ask before signing up.
          </h2>
        </div>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details
              key={i}
              className="card-surface p-5 cursor-pointer group"
              data-testid={`lp-faq-${i}`}
            >
              <summary className="text-sm sm:text-base font-medium text-white list-none flex items-center justify-between gap-4">
                {f.q}
                <span className="text-[#FF9764] text-xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-sm text-neutral-400 leading-relaxed mt-3 pr-8">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FINAL CTA ─── */
function FinalCTA({ getSlot }) {
  const finalHeadline = getSlot(
    "lp_smallfleet_final_cta_headline_v1",
    "Try FreightPilot AI free for 7 days.",
  );
  return (
    <section className="py-20 sm:py-28 border-b border-white/10 relative overflow-hidden" data-testid="lp-final-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center relative z-10">
        <h2
          className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-5"
          data-testid="lp-final-cta-headline"
        >
          {finalHeadline}
        </h2>
        <p className="text-neutral-400 text-base sm:text-lg max-w-2xl mx-auto mb-8">
          Move your first 10 loads through the platform this week. See what
          your rates look like with AI-drafted counter-offers.
        </p>
        <a
          href={SIGNUP_URL}
          onClick={() => trackConversion("signup_click", { source: "lp_final" })}
          data-testid="lp-final-primary-cta"
          className="btn-primary rounded-full px-8 py-4 text-sm sm:text-base font-semibold inline-flex items-center gap-2"
        >
          Start your free trial <ArrowRight size={16} weight="bold" />
        </a>
        <p className="mt-4 text-xs text-neutral-500">
          Card on file, $0 today · Cancel anytime · US-based support
        </p>
      </div>
      <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[600px] h-64 bg-[#FF5A1F]/10 rounded-full blur-[100px] pointer-events-none" />
    </section>
  );
}

/* ─── FOOTER (minimal — 3 legal links only) ─── */
function MinimalFooter() {
  return (
    <footer className="py-8 sm:py-10" data-testid="lp-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
        <div className="flex items-center gap-2">
          <Logo variant="sm" /> © {new Date().getFullYear()} FreightPilot AI
        </div>
        <div className="flex items-center gap-5">
          <Link to="/roadmap" data-testid="footer-roadmap-link" className="hover:text-white transition-colors">Roadmap</Link>
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
        </div>
      </div>
    </footer>
  );
}

/* ─── STICKY CTA (mobile — always visible on scroll) ─── */
function StickyCTA() {
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-[#0A0A0A]/95 backdrop-blur-md border-t border-white/10 p-3"
      data-testid="lp-sticky-cta"
    >
      <a
        href={SIGNUP_URL}
        onClick={() => trackConversion("signup_click", { source: "lp_sticky_mobile" })}
        className="w-full rounded-full py-3 text-sm font-semibold bg-[#FF5A1F] text-black flex items-center justify-center gap-2"
      >
        Start 7-day free trial <ArrowRight size={14} weight="bold" />
      </a>
    </div>
  );
}
